import React, { useState, useEffect } from 'react';
import { MdEdit, MdEmail } from 'react-icons/md';
import { FiSave, FiX } from 'react-icons/fi';
import { FiPlus, FiServer, FiMail, FiClock } from 'react-icons/fi';
import Layout from '../components/Layout';
import Toast, { ToastType } from '../components/Toast';
import Modal from '../components/Modal';
import HtmlEditor from '../components/HtmlEditor';
import { apiService } from '../services/api';

interface SMTPSettings {
  id: number;
  host: string;
  port: number;
  username: string;
  password_set: boolean;
  use_tls: boolean;
  use_ssl: boolean;
  from_email: string;
  from_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface EmailLayout {
  id: number;
  name: string;
  description: string;
  header_html: string;
  footer_html: string;
  footer_html_fr: string;
  default_styles: string;
  is_default: boolean;
  is_active: boolean;
  template_count: number;
  created_at: string;
  updated_at: string;
}

interface EmailTemplate {
  id: number;
  name: string;
  template_type: string;
  template_type_display: string;
  description: string;
  subject_en: string;
  content_en: string;
  subject_fr: string;
  content_fr: string;
  from_email: string | null;
  from_name: string;
  cc: string;
  bcc: string;
  layout: number | null;
  layout_name: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface EmailHistory {
  id: number;
  to_email: string;
  subject: string;
  template_name: string | null;
  status: string;
  status_display: string;
  language: string;
  sent_at: string | null;
  created_at: string;
}

const Emails: React.FC = () => {
  // Tab State
  const [activeTab, setActiveTab] = useState<'smtp' | 'layouts' | 'templates' | 'history'>('smtp');

  // SMTP State
  const [smtp, setSMTP] = useState<SMTPSettings | null>(null);
  const [loadingSMTP, setLoadingSMTP] = useState(false);
  const [editingSMTP, setEditingSMTP] = useState(false);
  const [smtpForm, setSMTPForm] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    use_tls: true,
    use_ssl: false,
    from_email: '',
    from_name: '',
    is_active: false
  });

  // Email Layout State (Single layout)
  const [layout, setLayout] = useState<EmailLayout | null>(null);
  const [loadingLayout, setLoadingLayout] = useState(false);
  const [editingLayout, setEditingLayout] = useState(false);
  const [layoutForm, setLayoutForm] = useState({
    name: '',
    description: '',
    header_html: '',
    footer_html: '',
    footer_html_fr: '',
    default_styles: '',
    is_active: true
  });

  // Email Templates State
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    template_type: 'custom',
    description: '',
    subject_en: '',
    content_en: '',
    subject_fr: '',
    content_fr: '',
    from_email: '',
    from_name: '',
    cc: '',
    bcc: '',
    layout: null as number | null,
    is_active: true
  });

  // Email History State
  const [history, setHistory] = useState<EmailHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);

  // Preview State
  const [showPreview, setShowPreview] = useState(false);
  const [previewLang, setPreviewLang] = useState<'en' | 'fr'>('en');
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  useEffect(() => {
    fetchSMTP();
    fetchLayout();
    fetchTemplates();
    fetchHistory();
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [historyPage]);

  // SMTP Functions
  const fetchSMTP = async () => {
    setLoadingSMTP(true);
    try {
      const response = await apiService.getSMTPSettings();
      if (response.success && response.data) {
        setSMTP(response.data);
        setSMTPForm({
          host: response.data.host || '',
          port: response.data.port || 587,
          username: response.data.username || '',
          password: '',
          use_tls: response.data.use_tls ?? true,
          use_ssl: response.data.use_ssl ?? false,
          from_email: response.data.from_email || '',
          from_name: response.data.from_name || '',
          is_active: response.data.is_active ?? false
        });
      }
    } catch (error) {
      setToast({ message: 'Failed to load SMTP settings', type: 'error' });
    } finally {
      setLoadingSMTP(false);
    }
  };

  const handleSaveSMTP = async () => {
    try {
      const dataToSend = { ...smtpForm };
      if (!dataToSend.password) {
        delete (dataToSend as any).password;
      }
      const response = await apiService.updateSMTPSettings(dataToSend);
      if (response.success) {
        setToast({ message: 'SMTP settings updated successfully', type: 'success' });
        setEditingSMTP(false);
        fetchSMTP();
      } else {
        setToast({ message: 'Failed to update SMTP settings', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to update SMTP settings', type: 'error' });
    }
  };

  // Email Layout Functions (Single layout)
  const fetchLayout = async () => {
    setLoadingLayout(true);
    try {
      const response = await apiService.getEmailLayouts();
      if (response.success && response.data && response.data.length > 0) {
        const firstLayout = response.data[0];
        setLayout(firstLayout);
        setLayoutForm({
          name: firstLayout.name,
          description: firstLayout.description || '',
          header_html: firstLayout.header_html,
          footer_html: firstLayout.footer_html,
          footer_html_fr: firstLayout.footer_html_fr || '',
          default_styles: firstLayout.default_styles || '',
          is_active: firstLayout.is_active
        });
      }
    } catch (error) {
      setToast({ message: 'Failed to load email layout', type: 'error' });
    } finally {
      setLoadingLayout(false);
    }
  };

  const handleEditLayoutToggle = () => {
    setEditingLayout(!editingLayout);
  };

  const handleCancelLayoutEdit = () => {
    if (layout) {
      setLayoutForm({
        name: layout.name,
        description: layout.description || '',
        header_html: layout.header_html,
        footer_html: layout.footer_html,
        footer_html_fr: layout.footer_html_fr || '',
        default_styles: layout.default_styles || '',
        is_active: layout.is_active
      });
    }
    setEditingLayout(false);
  };

  const handleSaveLayout = async () => {
    if (!layout) return;
    
    try {
      const response = await apiService.updateEmailLayout(layout.id, layoutForm);
      if (response.success) {
        setToast({ message: 'Email layout updated successfully', type: 'success' });
        setEditingLayout(false);
        fetchLayout();
      } else {
        setToast({ message: 'Failed to update email layout', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to save email layout', type: 'error' });
    }
  };

  // Email Templates Functions
  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await apiService.getEmailTemplates();
      if (response.success && response.data) {
        setTemplates(response.data);
      }
    } catch (error) {
      setToast({ message: 'Failed to load email templates', type: 'error' });
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleEditTemplate = async (template: EmailTemplate) => {
    // Fetch full template details including content
    try {
      const response = await apiService.getEmailTemplate(template.id);
      if (response.success && response.data) {
        const fullTemplate = response.data;
        setEditingTemplate(fullTemplate);
        setTemplateForm({
          name: fullTemplate.name,
          template_type: fullTemplate.template_type,
          description: fullTemplate.description || '',
          subject_en: fullTemplate.subject_en,
          content_en: fullTemplate.content_en || '',
          subject_fr: fullTemplate.subject_fr || '',
          content_fr: fullTemplate.content_fr || '',
          from_email: fullTemplate.from_email || '',
          from_name: fullTemplate.from_name || '',
          cc: fullTemplate.cc || '',
          bcc: fullTemplate.bcc || '',
          layout: fullTemplate.layout || null,
          is_active: fullTemplate.is_active
        });
        setShowTemplateModal(true);
      } else {
        setToast({ message: 'Failed to load template details', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to load template details', type: 'error' });
    }
  };

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      template_type: 'custom',
      description: '',
      subject_en: '',
      content_en: '',
      subject_fr: '',
      content_fr: '',
      from_email: '',
      from_name: '',
      cc: '',
      bcc: '',
      layout: layout?.id || null,
      is_active: true
    });
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject_en || !templateForm.content_en) {
      setToast({ message: 'Please fill required fields (Name, English Subject, English Content)', type: 'error' });
      return;
    }

    try {
      // Ensure layout is set to default layout
      const dataToSave = {
        ...templateForm,
        layout: layout?.id || null
      };

      let response;
      if (editingTemplate) {
        response = await apiService.updateEmailTemplate(editingTemplate.id, dataToSave);
      } else {
        response = await apiService.createEmailTemplate(dataToSave);
      }

      if (response.success) {
        setToast({ message: `Email template ${editingTemplate ? 'updated' : 'created'} successfully`, type: 'success' });
        setShowTemplateModal(false);
        fetchTemplates();
      } else {
        setToast({ message: response.error || 'Failed to save email template', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to save email template', type: 'error' });
    }
  };

  // Email History Functions
  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await apiService.getEmailHistory(historyPage);
      if (response.success && response.data) {
        setHistory(response.data.results || []);
        setHistoryTotal(response.data.count || 0);
      }
    } catch (error) {
      setToast({ message: 'Failed to load email history', type: 'error' });
    } finally {
      setLoadingHistory(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    }
  };

  // Preview Functions
  const handlePreviewTemplate = async (template: EmailTemplate) => {
    try {
      const response = await apiService.getEmailTemplate(template.id);
      if (response.success && response.data) {
        setPreviewTemplate(response.data);
        setPreviewLang('en');
        setShowPreview(true);
      } else {
        setToast({ message: 'Failed to load template for preview', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Failed to load template for preview', type: 'error' });
    }
  };

  // Reactive preview content - updates when templateForm or previewLang changes
  const previewContent = React.useMemo(() => {
    if (!previewTemplate || !layout) return '';

    // Use live templateForm data if editing, otherwise use previewTemplate
    const isEditing = showTemplateModal && editingTemplate?.id === previewTemplate.id;
    const bodyContent = isEditing
      ? (previewLang === 'en' ? templateForm.content_en : templateForm.content_fr)
      : (previewLang === 'en' ? previewTemplate.content_en : previewTemplate.content_fr);

    // Get correct footer based on language
    const footerContent = previewLang === 'fr' && layout.footer_html_fr 
      ? layout.footer_html_fr 
      : layout.footer_html;

    // Combine layout header + body + layout footer
    const fullContent = `
      ${layout.header_html}
      <div style="padding: 30px 20px; max-width: 600px; margin: 0 auto;">
        ${bodyContent}
      </div>
      ${footerContent}
    `;

    // Replace placeholders with sample data
    return fullContent
      .replace(/\{\{app_name\}\}/g, 'Teamther.ai')
      .replace(/\{\{first_name\}\}/g, 'John')
      .replace(/\{\{last_name\}\}/g, 'Doe')
      .replace(/\{\{user_name\}\}/g, 'John Doe')
      .replace(/\{\{user_email\}\}/g, 'john.doe@example.com')
      .replace(/\{\{code\}\}/g, '123456')
      .replace(/\{\{dashboard_url\}\}/g, '#')
      .replace(/\{\{verification_link\}\}/g, '#')
      .replace(/\{\{reset_link\}\}/g, '#')
      .replace(/\{\{support_url\}\}/g, '#')
      .replace(/\{\{privacy_url\}\}/g, '#')
      .replace(/\{\{terms_url\}\}/g, '#');
  }, [previewTemplate, layout, showTemplateModal, editingTemplate, previewLang, templateForm.content_en, templateForm.content_fr]);

  // Reactive preview subject - updates when templateForm or previewLang changes
  const previewSubject = React.useMemo(() => {
    if (!previewTemplate) return '';

    // Use live templateForm data if editing, otherwise use previewTemplate
    const isEditing = showTemplateModal && editingTemplate?.id === previewTemplate.id;
    return isEditing
      ? (previewLang === 'en' ? templateForm.subject_en : templateForm.subject_fr)
      : (previewLang === 'en' ? previewTemplate.subject_en : previewTemplate.subject_fr);
  }, [previewTemplate, showTemplateModal, editingTemplate, previewLang, templateForm.subject_en, templateForm.subject_fr]);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Email Settings</h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('smtp')}
            className={`pb-3 px-4 text-sm font-medium transition ${
              activeTab === 'smtp'
                ? 'border-b-2 border-brand-500 text-brand-600 dark:text-brand-400'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <FiServer className="h-4 w-4" />
              SMTP Config
            </div>
          </button>
          <button
            onClick={() => setActiveTab('layouts')}
            className={`pb-3 px-4 text-sm font-medium transition ${
              activeTab === 'layouts'
                ? 'border-b-2 border-brand-500 text-brand-600 dark:text-brand-400'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <MdEdit className="h-4 w-4" />
              Layouts
            </div>
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`pb-3 px-4 text-sm font-medium transition ${
              activeTab === 'templates'
                ? 'border-b-2 border-brand-500 text-brand-600 dark:text-brand-400'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <FiMail className="h-4 w-4" />
              Templates
            </div>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`pb-3 px-4 text-sm font-medium transition ${
              activeTab === 'history'
                ? 'border-b-2 border-brand-500 text-brand-600 dark:text-brand-400'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2">
              <FiClock className="h-4 w-4" />
              History
            </div>
          </button>
        </div>

        {/* SMTP Settings Card */}
        {activeTab === 'smtp' && (
        <div className="rounded-xl bg-white p-6 shadow-lg dark:bg-navy-800">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <FiServer className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">SMTP Configuration</h2>
            </div>
            {!editingSMTP ? (
              <button
                onClick={() => setEditingSMTP(true)}
                className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
              >
                <MdEdit className="h-4 w-4" />
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingSMTP(false)}
                  className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <FiX className="h-4 w-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveSMTP}
                  className="flex items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-white hover:bg-green-600"
                >
                  <FiSave className="h-4 w-4" />
                  Save
                </button>
              </div>
            )}
          </div>

          {loadingSMTP ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
            </div>
          ) : editingSMTP ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Host *</label>
                <input
                  type="text"
                  value={smtpForm.host}
                  onChange={(e) => setSMTPForm({ ...smtpForm, host: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  placeholder="smtp.example.com"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Port *</label>
                <input
                  type="number"
                  value={smtpForm.port}
                  onChange={(e) => setSMTPForm({ ...smtpForm, port: parseInt(e.target.value) || 587 })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Username *</label>
                <input
                  type="text"
                  value={smtpForm.username}
                  onChange={(e) => setSMTPForm({ ...smtpForm, username: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Password {smtp?.password_set && '(leave blank to keep current)'}
                </label>
                <input
                  type="password"
                  value={smtpForm.password}
                  onChange={(e) => setSMTPForm({ ...smtpForm, password: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  placeholder={smtp?.password_set ? '••••••••' : 'Enter password'}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">From Email *</label>
                <input
                  type="email"
                  value={smtpForm.from_email}
                  onChange={(e) => setSMTPForm({ ...smtpForm, from_email: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">From Name</label>
                <input
                  type="text"
                  value={smtpForm.from_name}
                  onChange={(e) => setSMTPForm({ ...smtpForm, from_name: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={smtpForm.use_tls}
                    onChange={(e) => setSMTPForm({ ...smtpForm, use_tls: e.target.checked, use_ssl: false })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Use TLS</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={smtpForm.use_ssl}
                    onChange={(e) => setSMTPForm({ ...smtpForm, use_ssl: e.target.checked, use_tls: false })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Use SSL</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={smtpForm.is_active}
                    onChange={(e) => setSMTPForm({ ...smtpForm, is_active: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                </label>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Host</p>
                <p className="font-medium text-gray-900 dark:text-white">{smtp?.host || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Port</p>
                <p className="font-medium text-gray-900 dark:text-white">{smtp?.port || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Username</p>
                <p className="font-medium text-gray-900 dark:text-white">{smtp?.username || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${smtp?.is_active
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                  {smtp?.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">From Email</p>
                <p className="font-medium text-gray-900 dark:text-white">{smtp?.from_email || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">From Name</p>
                <p className="font-medium text-gray-900 dark:text-white">{smtp?.from_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Encryption</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {smtp?.use_tls ? 'TLS' : smtp?.use_ssl ? 'SSL' : 'None'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Password</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {smtp?.password_set ? '••••••••' : 'Not set'}
                </p>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Email Layout Card */}
        {activeTab === 'layouts' && (
        <div className="rounded-xl bg-white p-6 shadow-lg dark:bg-navy-800">
          <div className="mb-4 flex items-center justify-end">
            {!editingLayout ? (
              <button
                onClick={handleEditLayoutToggle}
                className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
              >
                <MdEdit className="h-4 w-4" />
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleCancelLayoutEdit}
                  className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <FiX className="h-4 w-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSaveLayout}
                  className="flex items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-white hover:bg-green-600"
                >
                  <FiSave className="h-4 w-4" />
                  Save
                </button>
              </div>
            )}
          </div>

          {loadingLayout ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
            </div>
          ) : !layout ? (
            <div className="py-12 text-center">
              <MdEdit className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-gray-600 dark:text-gray-400">No email layout found</p>
            </div>
          ) : editingLayout ? (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Layout Name *</label>
                  <input
                    type="text"
                    value={layoutForm.name}
                    onChange={(e) => setLayoutForm({ ...layoutForm, name: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                    placeholder="e.g., Default Layout"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                  <input
                    type="text"
                    value={layoutForm.description}
                    onChange={(e) => setLayoutForm({ ...layoutForm, description: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                    placeholder="Brief description"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Header HTML</label>
                <HtmlEditor
                  value={layoutForm.header_html}
                  onChange={(html) => setLayoutForm({ ...layoutForm, header_html: html })}
                  placeholder="Enter header HTML (logo, branding, etc.)..."
                  minHeight="200px"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Use placeholders: {'{'}{'{'} app_name {'}'}{'}'},  {'{'}{'{'} privacy_url {'}'}{'}'},  {'{'}{'{'} terms_url {'}'}{'}' }
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Footer HTML (English)</label>
                <HtmlEditor
                  value={layoutForm.footer_html}
                  onChange={(html) => setLayoutForm({ ...layoutForm, footer_html: html })}
                  placeholder="Enter footer HTML (copyright, links, etc.)..."
                  minHeight="200px"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Use placeholders: {'{'}{'{'} app_name {'}'}{'}'},  {'{'}{'{'} support_url {'}'}{'}'},  {'{'}{'{'} privacy_url {'}'}{'}'},  {'{'}{'{'} terms_url {'}'}{'}' }
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Footer HTML (French)</label>
                <HtmlEditor
                  value={layoutForm.footer_html_fr}
                  onChange={(html) => setLayoutForm({ ...layoutForm, footer_html_fr: html })}
                  placeholder="Enter footer HTML in French (copyright, links, etc.)..."
                  minHeight="200px"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Version française du pied de page. Utilisez les mêmes placeholders
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Default Styles (CSS)</label>
                <textarea
                  value={layoutForm.default_styles}
                  onChange={(e) => setLayoutForm({ ...layoutForm, default_styles: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  placeholder="body { font-family: Arial, sans-serif; }"
                  rows={8}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  CSS styles that will be applied to all emails using this layout
                </p>
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={layoutForm.is_active}
                  onChange={(e) => setLayoutForm({ ...layoutForm, is_active: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
              </label>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Header Preview</p>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-navy-700">
                  <div dangerouslySetInnerHTML={{ __html: layout.header_html || '<p class="text-gray-400">No header HTML</p>' }} />
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Footer Preview (English)</p>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-navy-700">
                  <div dangerouslySetInnerHTML={{ __html: layout.footer_html || '<p class="text-gray-400">No footer HTML</p>' }} />
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Footer Preview (French)</p>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-navy-700">
                  <div dangerouslySetInnerHTML={{ __html: layout.footer_html_fr || '<p class="text-gray-400">No French footer HTML</p>' }} />
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Email Templates Card */}
        {activeTab === 'templates' && (
        <div className="rounded-xl bg-white p-6 shadow-lg dark:bg-navy-800">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <FiMail className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Email Templates</h2>
            </div>
            <button
              onClick={handleAddTemplate}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
            >
              <FiPlus className="h-4 w-4" />
              Add Template
            </button>
          </div>

          {loadingTemplates ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
            </div>
          ) : templates.length === 0 ? (
            <div className="py-12 text-center">
              <MdEmail className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-gray-600 dark:text-gray-400">No email templates yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {[...templates]
                .sort((a, b) => {
                  // Extract leading numbers from descriptions
                  const getLeadingNumber = (desc: string) => {
                    const match = desc.match(/^(\d+)/);
                    return match ? parseInt(match[1], 10) : 999999;
                  };
                  
                  const numA = getLeadingNumber(a.description || '');
                  const numB = getLeadingNumber(b.description || '');
                  
                  // Sort by number first, then by full description if numbers are equal
                  if (numA !== numB) {
                    return numA - numB;
                  }
                  return (a.description || '').localeCompare(b.description || '');
                })
                .map((template) => (
                  <div
                    key={template.id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 transition hover:shadow-md dark:border-gray-700"
                  >
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-semibold text-gray-600 dark:text-gray-400">{template.description || '—'}</span>
                          <span className="text-sm text-gray-300 dark:text-gray-600">|</span>
                          <h3 className="truncate text-base font-bold text-gray-900 dark:text-white">{template.name}</h3>
                          <span className="shrink-0 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                            {template.template_type_display}
                          </span>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${template.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                            {template.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4 flex shrink-0 gap-2">
                      <button
                        onClick={() => handlePreviewTemplate(template)}
                        className="flex items-center gap-1.5 rounded-lg bg-blue-100 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50"
                      >
                        <FiMail className="h-3.5 w-3.5" />
                        Preview
                      </button>
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className="flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                      >
                        <MdEdit className="h-3.5 w-3.5" />
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
        )}

        {/* Email History Card */}
        {activeTab === 'history' && (
        <div className="rounded-xl bg-white p-6 shadow-lg dark:bg-navy-800">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/30">
              <FiClock className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Email History</h2>
            <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {historyTotal} emails
            </span>
          </div>

          {loadingHistory ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="py-12 text-center">
              <FiClock className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-gray-600 dark:text-gray-400">No emails sent yet</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="pb-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">To</th>
                      <th className="pb-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Subject</th>
                      <th className="pb-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Template</th>
                      <th className="pb-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
                      <th className="pb-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((email) => (
                      <tr key={email.id} className="border-b border-gray-100 dark:border-gray-700">
                        <td className="py-3 text-sm text-gray-900 dark:text-white">{email.to_email}</td>
                        <td className="py-3 text-sm text-gray-600 dark:text-gray-400">
                          {email.subject.substring(0, 50)}...
                        </td>
                        <td className="py-3 text-sm text-gray-600 dark:text-gray-400">
                          {email.template_name || '-'}
                        </td>
                        <td className="py-3">
                          <span className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(email.status)}`}>
                            {email.status_display}
                          </span>
                        </td>
                        <td className="py-3 text-sm text-gray-600 dark:text-gray-400">
                          {new Date(email.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {historyTotal > 50 && (
                <div className="mt-4 flex justify-center gap-2">
                  <button
                    onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
                    disabled={historyPage === 1}
                    className="rounded-lg border border-gray-300 px-3 py-1 text-sm disabled:opacity-50 dark:border-gray-600"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400">
                    Page {historyPage}
                  </span>
                  <button
                    onClick={() => setHistoryPage(historyPage + 1)}
                    disabled={historyPage * 50 >= historyTotal}
                    className="rounded-lg border border-gray-300 px-3 py-1 text-sm disabled:opacity-50 dark:border-gray-600"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        )}
      </div>

      {/* Template Edit Modal */}
      <Modal
        isOpen={showTemplateModal}
        title={editingTemplate ? 'Edit Email Template' : 'Add Email Template'}
        onClose={() => setShowTemplateModal(false)}
        size="xl"
      >
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Template Name *
                {editingTemplate && (
                  <span className="ml-2 text-xs text-orange-600 dark:text-orange-400">(Cannot be changed)</span>
                )}
              </label>
              <input
                type="text"
                value={templateForm.name}
                onChange={(e) => !editingTemplate && setTemplateForm({ ...templateForm, name: e.target.value })}
                disabled={!!editingTemplate}
                className={`w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white ${editingTemplate ? 'cursor-not-allowed bg-gray-100 opacity-75 dark:bg-navy-900' : ''
                  }`}
                placeholder="e.g., welcome"
              />
              {editingTemplate && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Template name is used as identifier for sending emails
                </p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Type</label>
              <select
                value={templateForm.template_type}
                onChange={(e) => setTemplateForm({ ...templateForm, template_type: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
              >
                <option value="welcome">Welcome Email</option>
                <option value="password_reset">Password Reset</option>
                <option value="email_verification">Email Verification</option>
                <option value="custom">Custom Email</option>
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <input
              type="text"
              value={templateForm.description}
              onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
              placeholder="e.g., 1. Welcome email sent after registration"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Start with a number (e.g., "1.", "2.") for ordering in the list
            </p>
          </div>

          {/* English Version */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-900/20">
            <h4 className="mb-3 font-medium text-blue-800 dark:text-blue-300">🇬🇧 English Version</h4>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Subject *</label>
                <input
                  type="text"
                  value={templateForm.subject_en}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject_en: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  placeholder="Email subject line"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Content *</label>
                <HtmlEditor
                  value={templateForm.content_en}
                  onChange={(html) => setTemplateForm({ ...templateForm, content_en: html })}
                  placeholder="Enter email content here..."
                  minHeight="250px"
                />
              </div>
            </div>
          </div>

          {/* French Version */}
          <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-900 dark:bg-purple-900/20">
            <h4 className="mb-3 font-medium text-purple-800 dark:text-purple-300">🇫🇷 French Version</h4>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Subject *</label>
                <input
                  type="text"
                  value={templateForm.subject_fr}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject_fr: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  placeholder="Objet de l'email"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Content *</label>
                <HtmlEditor
                  value={templateForm.content_fr}
                  onChange={(html) => setTemplateForm({ ...templateForm, content_fr: html })}
                  placeholder="Entrez le contenu de l'email ici..."
                  minHeight="250px"
                />
              </div>
            </div>
          </div>

          {/* Additional Settings */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">CC (comma separated)</label>
              <input
                type="text"
                value={templateForm.cc}
                onChange={(e) => setTemplateForm({ ...templateForm, cc: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">BCC (comma separated)</label>
              <input
                type="text"
                value={templateForm.bcc}
                onChange={(e) => setTemplateForm({ ...templateForm, bcc: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
              />
            </div>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={templateForm.is_active}
              onChange={(e) => setTemplateForm({ ...templateForm, is_active: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
          </label>

          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => setShowTemplateModal(false)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveTemplate}
              className="rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
            >
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreview}
        title={`Preview: ${previewTemplate?.name || 'Email Template'}`}
        onClose={() => setShowPreview(false)}
        size="xl"
      >
        <div className="space-y-4">
          {/* Language Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Language:</span>
              <div className="flex rounded-lg border border-gray-300 dark:border-gray-600">
                <button
                  onClick={() => setPreviewLang('en')}
                  className={`px-4 py-2 text-sm font-medium transition ${previewLang === 'en'
                    ? 'bg-brand-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-navy-700 dark:text-gray-300'
                    } rounded-l-lg`}
                >
                  🇬🇧 English
                </button>
                <button
                  onClick={() => setPreviewLang('fr')}
                  className={`px-4 py-2 text-sm font-medium transition ${previewLang === 'fr'
                    ? 'bg-brand-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-navy-700 dark:text-gray-300'
                    } rounded-r-lg`}
                >
                  🇫🇷 French
                </button>
              </div>
            </div>
            <span className={`rounded-full px-2 py-1 text-xs font-medium ${previewTemplate?.is_active
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
              }`}>
              {previewTemplate?.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>

          {/* Subject Preview */}
          <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
            <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Subject</p>
            <p className="font-medium text-gray-900 dark:text-white">
              {previewSubject}
            </p>
          </div>

          {/* Email Content Preview */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-navy-700">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Email Content Preview</p>
            </div>
            <div
              className="max-h-[500px] overflow-auto bg-white px-4 py-3 dark:bg-navy-900"
              style={{
                color: 'inherit',
                fontFamily: 'inherit'
              }}
              dangerouslySetInnerHTML={{ __html: previewContent }}
            />
          </div>

          {/* Sample Data Notice */}
          <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
            <p className="text-xs text-blue-700 dark:text-blue-400">
              <strong>Note:</strong> This preview uses sample data for placeholders like {'{'}{'{'} first_name {'}'}{'}'},  {'{'}{'{'} code {'}'}{'}'},  etc.
            </p>
          </div>

          {/* Close Button */}
          <div className="flex justify-end pt-2">
            <button
              onClick={() => setShowPreview(false)}
              className="rounded-lg bg-gray-100 px-6 py-2 font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Layout>
  );
};

export default Emails;
